import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { AsyncEntity } from 'src/global';
import createAsyncThunkFromFetch from 'src/store/middlewares/createAsyncThunkFromFetch';
import {
  createContent as createContentAPI,
  createContentLocaleAPI,
  deleteContent as deleteContentAPI,
  deleteContentLocaleAPI,
  fetchContentLocalesAPI,
  getContent,
  getContents,
  updateContent as updateContentAPI,
  updateContentLocaleAPI,
} from './contents.api';
import { ContentCard } from './contents.types';

const name = 'contents';

const contentEntityAdapter = createEntityAdapter<AsyncEntity<ContentCard>>({
  selectId: content => content.id,
});

export const contentsSelectors = contentEntityAdapter.getSelectors();

export const fetchContent = createAsyncThunkFromFetch(`${name}/fetchContent`, getContent);
export const fetchContents = createAsyncThunkFromFetch(`${name}/fetchContents`, getContents);
export const deleteContent = createAsyncThunkFromFetch(`${name}/deleteContent`, deleteContentAPI);
export const createContent = createAsyncThunkFromFetch(`${name}/createContent`, createContentAPI);
export const updateContent = createAsyncThunkFromFetch(`${name}/updateContent`, updateContentAPI);
export const fetchContentLocales = createAsyncThunkFromFetch(`${name}/fetchCardLocale`, fetchContentLocalesAPI);
export const createContentLocale = createAsyncThunkFromFetch(`${name}/createContentLocale`, createContentLocaleAPI);
export const updateContentLocale = createAsyncThunkFromFetch(`${name}/updateContentLocale`, updateContentLocaleAPI);
export const deleteContentLocale = createAsyncThunkFromFetch(`${name}/deleteContentLocale`, deleteContentLocaleAPI);

const contentsSlice = createSlice({
  name,
  initialState: contentEntityAdapter.getInitialState({
    isPending: false,
    hasError: false,
  }),
  reducers: {},
  extraReducers: builder => {
    // Contents
    builder.addCase(fetchContents.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(fetchContents.fulfilled, (state, { payload }) => {
      contentEntityAdapter.setAll(state, payload);
      state.isPending = false;
      state.hasError = false;
    });
    builder.addCase(fetchContents.rejected, state => {
      state.isPending = false;
      state.hasError = true;
    });
    builder.addCase(deleteContent.pending, (state, { meta }) => {
      contentEntityAdapter.updateOne(state, {
        id: meta.arg.contentId,
        changes: { isPending: true },
      });
    });
    builder.addCase(deleteContent.fulfilled, (state, { meta }) => {
      contentEntityAdapter.removeOne(state, meta.arg.contentId);
    });
    builder.addCase(deleteContent.rejected, (state, { meta }) => {
      contentEntityAdapter.updateOne(state, {
        id: meta.arg.contentId,
        changes: { isPending: false, hasError: true },
      });
    });
    builder.addCase(createContent.fulfilled, (state, { payload }) => {
      contentEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(fetchContent.fulfilled, (state, { payload }) => {
      contentEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(updateContent.pending, (state, { meta }) => {
      contentEntityAdapter.updateOne(state, {
        id: meta.arg.contentId,
        changes: { isPending: true },
      });
    });
    builder.addCase(updateContent.fulfilled, (state, { meta, payload }) => {
      contentEntityAdapter.updateOne(state, {
        id: meta.arg.contentId,
        changes: { ...payload, isPending: false },
      });
    });
    builder.addCase(fetchContentLocales.fulfilled, (state, { meta, payload }) => {
      contentEntityAdapter.updateOne(state, {
        id: meta.arg.contentId,
        changes: { locales: payload, isPending: false },
      });
    });
    builder.addCase(createContentLocale.fulfilled, (state, { meta, payload }) => {
      const content = contentsSelectors.selectById(state, meta.arg.contentId) as ContentCard;
      contentEntityAdapter.updateOne(state, {
        id: meta.arg.contentId,
        changes: { locales: [payload, ...content.locales], isPending: false },
      });
    });
    builder.addCase(updateContentLocale.fulfilled, (state, { meta, payload }) => {
      const content = contentsSelectors.selectById(state, meta.arg.contentId) as ContentCard;
      contentEntityAdapter.updateOne(state, {
        id: meta.arg.contentId,
        changes: { locales: content.locales.map(l => (l.id === meta.arg.localeId ? payload : l)), isPending: false },
      });
    });
    builder.addCase(deleteContentLocale.fulfilled, (state, { meta }) => {
      const content = contentsSelectors.selectById(state, meta.arg.contentId) as ContentCard;
      contentEntityAdapter.updateOne(state, {
        id: meta.arg.contentId,
        changes: { locales: content.locales.filter(l => l.id !== meta.arg.localeId), isPending: false },
      });
    });
  },
});

const { reducer } = contentsSlice;

export default reducer;
